import { useRosterProfile } from '@/hooks/equip/useRosterProfile'
import { useEffectiveEmploymentStatus } from '@/hooks/equip/useEffectiveEmploymentStatus'
import { PhysicianFramework } from '@/pages/physician/PhysicianFramework'
import { NonPhysicianFramework } from '@/pages/non-physician/NonPhysicianFramework'
import type { NonPhysicianRole } from '@/pages/non-physician/NonPhysicianFramework'
import { useImpersonation } from '@/context/ImpersonationContext'
import { useRBAC } from '@/hooks/useRBAC'
import { Calculator, BarChart3 } from 'lucide-react'

/**
 * ClinicianFrameworkGateway
 *
 * Routes equip.read clinicians to the correct framework track based on their
 * roster profile (physician vs APC) and locks APC clinicians to their specific
 * role — preventing cross-cohort data exposure.
 *
 * Physician clinicians get a readOnly PhysicianFramework (leader CTAs removed).
 * APC clinicians get NonPhysicianFramework locked to their roleId.
 *
 * Admins can set a selectedPersona via the nav bar persona dropdown to test
 * what different clinician types see without needing their credentials.
 */
export function ClinicianFrameworkGateway() {
  const { data: profile, isLoading, error } = useRosterProfile()
  const { selectedPersona } = useImpersonation()
  const { isAdmin } = useRBAC()
  const { effectiveEmploymentStatus } = useEffectiveEmploymentStatus()

  const adminNavItems = [
    { id: 'framework', label: 'Framework', path: '/framework', icon: <Calculator className="w-4 h-4" /> },
    { id: 'performance', label: 'Performance Dashboard', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
  ]

  const readOnlyNavItems = [
    { id: 'framework', label: 'Framework', path: '/framework', icon: <Calculator className="w-4 h-4" /> },
    { id: 'performance', label: 'Performance Dashboard', path: '/performance', icon: <BarChart3 className="w-4 h-4" /> },
  ]

  // Loading state — only show spinner when real profile is loading AND no persona is set
  if (isLoading && !selectedPersona && !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-eventus-teal border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 text-sm">Loading your framework...</p>
        </div>
      </div>
    )
  }

  // Admin with no persona selected → show unlocked NonPhysicianFramework (leadership view)
  if (isAdmin && !selectedPersona) {
    return <NonPhysicianFramework navItems={adminNavItems} showPersonaDropdown showHome employmentStatus={effectiveEmploymentStatus} />
  }

  // Use persona if admin has set one, otherwise use real API result
  const effectiveProfile = selectedPersona ?? profile

  // Error state — only show if real API errored AND no persona override
  if (error && !selectedPersona) {
    return <FrameworkFallback reason="error" />
  }

  // No profile or null roleId — graceful fallback (FRAME-05)
  if (!effectiveProfile || effectiveProfile.roleId === null) {
    return <FrameworkFallback reason="no-profile" />
  }

  // Determine nav items and props based on role
  const navItems = isAdmin ? adminNavItems : readOnlyNavItems
  const showHome = isAdmin

  // Physician track (FRAME-02, FRAME-04)
  if (effectiveProfile.isPhysician === true) {
    const calcRole = effectiveProfile.roleId === 'psychiatry-physician' ? 'psychiatry-md' as const : undefined
    return <PhysicianFramework readOnly lockedCalculatorRole={calcRole} employmentStatus={effectiveEmploymentStatus} navItems={navItems} showPersonaDropdown={isAdmin} showHome={showHome} />
  }

  // APC track (FRAME-02, FRAME-03)
  return <NonPhysicianFramework lockedRoleId={effectiveProfile.roleId as NonPhysicianRole} employmentStatus={effectiveEmploymentStatus} navItems={navItems} showPersonaDropdown={isAdmin} showHome={showHome} />
}

function FrameworkFallback({ reason }: { reason: 'no-profile' | 'error' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md text-center">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Framework Not Available</h2>
        <p className="text-gray-600 text-sm leading-relaxed">
          {reason === 'error'
            ? 'We were unable to load your framework content. Please try refreshing the page or contact support if the issue persists.'
            : 'Framework content is not currently available for your role. If you believe this is an error, contact your administrator.'}
        </p>
      </div>
    </div>
  )
}
